// 🎨 Beautiful Signup Form - Trivana Red-Yellow Theme (Compact & Elegant)
import React, { useState } from "react";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaShieldAlt,
  FaHotel,
  FaCheckCircle,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const Signup = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const validateName = (name) => {
    return name.trim().length >= 2;
  };

  const validateEmail = (email) => {
    return email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
  };

  const validatePhone = (phone) => {
    return phone.match(/^\d{10,15}$/);
  };

  const validatePassword = (password) => {
    return password.length >= 8;
  };

  const validateConfirmPassword = (confirmPassword, password) => {
    return confirmPassword === password;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!validateName(name)) {
      newErrors.name = "Name must be at least 2 characters long";
    }
    if (!validateEmail(email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!validatePhone(phone)) {
      newErrors.phone = "Please enter a valid phone number (10-15 digits)";
    }
    if (!validatePassword(password)) {
      newErrors.password = "Password must be at least 8 characters long";
    }
    if (!validateConfirmPassword(confirmPassword, password)) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const { register } = await import("../api/authApi");
      const signupData = { name, email, phone, password };
      const response = await register(signupData);
      toast.success("Account created successfully! Please login to continue.");
      setTimeout(() => {
        navigate("/login");
      }, 1500);
    } catch (error) {
      console.error("Signup failed:", error);
      let errorMessage = "Signup failed. Please try again.";

      if (error.response && error.response.data) {
        // Extract message from object if it exists
        if (typeof error.response.data === "object") {
          errorMessage = error.response.data.message || errorMessage;
          // Set field-specific error if available
          if (error.response.data.field) {
            setErrors({ [error.response.data.field]: errorMessage });
            toast.error(errorMessage);
            setLoading(false);
            return;
          }
        } else if (typeof error.response.data === "string") {
          errorMessage = error.response.data;
        }
      }

      setErrors({ general: errorMessage });
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F5F5F5] via-[#FFFFFF] to-[#FFC400]/10 p-2">
      {/* Elegant Card */}
      <div className="w-full max-w-[340px] sm:max-w-[380px] bg-white/90 border-2 border-[#EE2E24]/20 backdrop-blur-sm rounded-xl shadow-xl shadow-[#EE2E24]/10 p-5 sm:p-6">
        {/* Elegant Header */}
        <div className="text-center mb-3">
          <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight bg-gradient-to-r from-[#EE2E24] to-[#FFC400] bg-clip-text text-transparent mb-1">
            Create your Trivana account
          </h2>
          <p className="text-[#2E2E2E]/70 text-[11px] sm:text-xs font-semibold">
            Sign up to get started
          </p>
        </div>

        {/* Error Message */}
        {errors.general && (
          <div className="mb-3 p-2 bg-[#EE2E24]/10 border-l-4 border-[#EE2E24] rounded text-[11px] text-[#EE2E24] font-bold">
            {errors.general}
          </div>
        )}

        {/* Signup Form */}
        <form onSubmit={handleSubmit} className="space-y-2.5">
          {/* Full Name */}
          <div>
            <label className="block text-xs font-bold text-[#2E2E2E] mb-1">
              Full Name
            </label>
            <div className="relative">
              <FaUser className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#EE2E24] text-xs" />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full pl-8 pr-2 py-2 border-2 border-gray-200 rounded-lg focus:border-[#EE2E24] focus:ring-2 focus:ring-[#FFC400]/20 outline-none transition-all text-sm"
                placeholder="Enter your full name"
              />
            </div>
            {errors.name && (
              <p className="mt-0.5 text-[10px] text-[#EE2E24] font-medium">
                {errors.name}
              </p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-xs font-bold text-[#2E2E2E] mb-1">
              Email
            </label>
            <div className="relative">
              <FaEnvelope className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#EE2E24] text-xs" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-8 pr-2 py-2 border-2 border-gray-200 rounded-lg focus:border-[#EE2E24] focus:ring-2 focus:ring-[#FFC400]/20 outline-none transition-all text-sm"
                placeholder="your@email.com"
              />
            </div>
            {errors.email && (
              <p className="mt-0.5 text-[10px] text-[#EE2E24] font-medium">
                {errors.email}
              </p>
            )}
          </div>

          {/* Phone */}
          <div>
            <label className="block text-xs font-bold text-[#2E2E2E] mb-1">
              Phone
            </label>
            <div className="relative">
              <FaPhone className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#EE2E24] text-xs" />
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full pl-8 pr-2 py-2 border-2 border-gray-200 rounded-lg focus:border-[#EE2E24] focus:ring-2 focus:ring-[#FFC400]/20 outline-none transition-all text-sm"
                placeholder="10-digit number"
              />
            </div>
            {errors.phone && (
              <p className="mt-0.5 text-[10px] text-[#EE2E24] font-medium">
                {errors.phone}
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="block text-xs font-bold text-[#2E2E2E] mb-1">
              Password
            </label>
            <div className="relative">
              <FaLock className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#EE2E24] text-xs" />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-8 pr-9 py-2 border-2 border-gray-200 rounded-lg focus:border-[#EE2E24] focus:ring-2 focus:ring-[#FFC400]/20 outline-none transition-all text-sm"
                placeholder="8+ characters"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#EE2E24] transition-colors"
              >
                {showPassword ? <FaEyeSlash size={13} /> : <FaEye size={13} />}
              </button>
            </div>
            {errors.password && (
              <p className="mt-0.5 text-[10px] text-[#EE2E24] font-medium">
                {errors.password}
              </p>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-xs font-bold text-[#2E2E2E] mb-1">
              Confirm
            </label>
            <div className="relative">
              <FaLock className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#EE2E24] text-xs" />
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full pl-8 pr-9 py-2 border-2 border-gray-200 rounded-lg focus:border-[#EE2E24] focus:ring-2 focus:ring-[#FFC400]/20 outline-none transition-all text-sm"
                placeholder="Re-enter password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#EE2E24] transition-colors"
              >
                {showConfirmPassword ? (
                  <FaEyeSlash size={13} />
                ) : (
                  <FaEye size={13} />
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="mt-0.5 text-[10px] text-[#EE2E24] font-medium">
                {errors.confirmPassword}
              </p>
            )}
          </div>

          {/* Sign Up Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-[#EE2E24] to-[#FFC400] text-white py-2 rounded-lg font-bold text-sm shadow-md shadow-[#EE2E24]/30 hover:shadow-lg hover:shadow-[#FFC400]/40 transition-all hover:scale-[1.02] disabled:opacity-50 mt-3"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Creating account...
              </div>
            ) : (
              <div className="flex items-center justify-center">
                <FaCheckCircle className="mr-1.5" />
                Create Account
              </div>
            )}
          </button>

          {/* Login Link */}
          <div className="text-center pt-2.5 border-t border-[#EE2E24]/10">
            <p className="text-[11px] text-gray-600">
              Already have an account?{" "}
              <a
                href="/login"
                className="text-[#EE2E24] font-bold hover:text-[#FFC400] transition-colors"
              >
                Sign In
              </a>
            </p>
          </div>

          {/* Security Badge */}
          <div className="flex items-center justify-center gap-1.5 text-[10px] text-gray-400 pt-2">
            <FaShieldAlt className="text-green-500 text-xs" />
            <span>Secured with encryption</span>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Signup;
